'use client';

import { Toast as BaseToast } from '@base-ui-components/react/toast';
import { cva } from 'class-variance-authority';
import { Loader2, X } from 'lucide-react';

import type { ToastType } from './types';
import { cn } from '../../utils/cn';
import './toast.css';

const toastVariants = cva(
  'ds-toast pointer-events-auto relative flex w-full items-center justify-between gap-3 overflow-hidden rounded-md border p-4 shadow-lg',
  {
    variants: {
      variant: {
        default: 'bg-background text-foreground border-border',
        success: 'bg-success text-success-foreground border-success/50',
        error:
          'bg-destructive text-destructive-foreground border-destructive/50',
        warning: 'bg-warning text-warning-foreground border-warning/50',
        info: 'bg-info text-info-foreground border-info/50',
        loading: 'bg-background text-foreground border-border',
      },
    },
    defaultVariants: { variant: 'default' },
  }
);

type ToastVariant = ToastType | 'default';

interface ToastItemProps {
  toast: BaseToast.Root.ToastObject;
}

function ToastItem({ toast }: ToastItemProps) {
  const variant: ToastVariant =
    (toast.type as ToastType | undefined) ?? 'default';

  return (
    <BaseToast.Root toast={toast} className={cn(toastVariants({ variant }))}>
      <div className="flex flex-1 items-start gap-3">
        {toast.type === 'loading' && (
          <Loader2 className="size-4 shrink-0 animate-spin" />
        )}
        <div className="flex flex-col gap-1">
          <BaseToast.Title className="text-sm font-semibold" />
          <BaseToast.Description className="text-sm opacity-80" />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <BaseToast.Action className="hover:bg-background/20 inline-flex h-8 items-center rounded-md border px-3 text-xs font-medium transition-colors" />
        <BaseToast.Close
          aria-label="Close"
          className="rounded-md p-1 opacity-60 transition-opacity hover:opacity-100"
        >
          <X className="size-4" />
        </BaseToast.Close>
      </div>
    </BaseToast.Root>
  );
}

export { ToastItem, toastVariants };
export type { ToastItemProps };
